/**
 * Frustration Monitor
 * 
 * This module detects user frustration points to help identify UX issues.
 * It tracks rage clicks, dead clicks, form abandonment, navigation thrashing,
 * error encounters, and other signs of user frustration.
 */

// Types for frustration events
/** windsurf generated code start */
/**
 * Main interface for frustration events tracked by the system
 */
interface FrustrationEvent {
  type: FrustrationType;
  timestamp: number;
  score: number; // How much this contributes to overall frustration (1-10)
  context: FrustrationEventContext;
}

/**
 * Extended context interface that includes all possible properties
 * for different types of frustration events
 */
interface FrustrationEventContext {
  // Element info for DOM-related frustration events
  element?: {
    tagName: string;
    id: string;
    className: string;
    text: string;
    href?: string;
    xpath: string;
  };
  // Current location
  location: string;
  // Error information for API or JS errors
  error?: string | Error | {
    message: string;
    name?: string;
    stack?: string;
  };
  // Request info for API errors
  url?: string;
  requestTime?: number;
  statusCode?: number;
  // Additional details for any event type
  details: Record<string, any>;
}

/**
 * Type guard to check if an object has an error property
 */
function hasError(context: any): context is { error: unknown } {
  return context && 'error' in context;
}

/**
 * Type guard to check if an object has a url property
 */
function hasUrl(context: any): context is { url: string } {
  return context && 'url' in context && typeof context.url === 'string';
}
/** windsurf generated code end */

enum FrustrationType {
  RAGE_CLICK = 'rage_click',
  DEAD_CLICK = 'dead_click',
  FORM_ABANDONMENT = 'form_abandonment',
  ERROR_ENCOUNTER = 'error_encounter',
  NAVIGATION_THRASHING = 'navigation_thrashing',
  SLOW_INTERACTION = 'slow_interaction',
  SESSION_THRASHING = 'session_thrashing',
  API_ERROR = 'api_error',
  JS_ERROR = 'js_error'
}

// Configuration options for frustration detection
interface FrustrationDetectionConfig {
  rageClickThreshold: number; // Number of clicks within timeframe to be considered rage
  rageClickTimeFrame: number; // Time window in ms for rage click detection
  deadClickEnabled: boolean;
  formAbandonmentEnabled: boolean;
  navigationThreshingEnabled: boolean;
  errorTrackingEnabled: boolean;
  slowInteractionThreshold: number; // Time in ms beyond which an interaction is considered slow
}

// Default configuration
const DEFAULT_CONFIG: FrustrationDetectionConfig = {
  rageClickThreshold: 3,
  rageClickTimeFrame: 1000,
  deadClickEnabled: true,
  formAbandonmentEnabled: true,
  navigationThreshingEnabled: true,
  errorTrackingEnabled: true,
  slowInteractionThreshold: 300
};

/**
 * Main class for monitoring user frustration
 */
export class FrustrationMonitor {
  private static instance: FrustrationMonitor;
  private config: FrustrationDetectionConfig;
  private isMonitoring: boolean = false;
  private readonly frustrationEvents: FrustrationEvent[] = [];
  
  // Tracking variables for different frustration types
/** windsurf generated code start */
  private mouseMovements: Array<{ x: number; y: number; timestamp: number }> = [];
  private clicks: Array<{ x: number; y: number; timestamp: number; element?: { text?: string; className?: string; tagName?: string } }> = [];
  private navigationHistory: Array<{ path: string; timestamp: number }> = [];
  private formInteractions: Record<string, { startTime: number; fields: string[] }> = {};
  private readonly lastNavigation = 0;
  private boundHandlers: Record<string, EventListener> = {};
  private elementTracker = new WeakMap<HTMLElement, { clickTime: number }>();
  private originalFetch: typeof fetch | null = null;
  private performanceObserver: PerformanceObserver | null = null;
  
  // Additional observers for DOM monitoring and cleanup
  private domObserver: MutationObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  
  // Rate limiting and throttling variables
  private lastMouseMoveTime = 0;
  private readonly debounceTimers: Record<string, number> = {};
  private readonly eventCounts: Record<string, { count: number; lastReset: number }> = {};
  private readonly interactionMeasurements: Record<string, number> = {};
  
  // Rate limiting variables required by setupRateLimiting method
  private lastDataCollection = 0;
  private collectionThrottleTime = 0;
  private maxEventsPerMinute = 0;
  private eventCountLastMinute = 0;
  private lastRateLimitReset = 0;
/** windsurf generated code end */
  
  // Rate limiting variables
/** windsurf generated code start */
  // Use WeakMap to prevent memory leaks when tracking slow interactions
  private slowInteractionTracker: WeakMap<Element, {startTime: number, interactionId: string}> = new WeakMap();
/** windsurf generated code end */
  
  // Performance observer is already defined above

  private constructor() {
    this.config = {...DEFAULT_CONFIG};
  }
  
  /**
   * Get the singleton instance of FrustrationMonitor
   */
  public static getInstance(): FrustrationMonitor {
    if (!FrustrationMonitor.instance) {
      FrustrationMonitor.instance = new FrustrationMonitor();
    }
    return FrustrationMonitor.instance;
  }
  
  /**
   * Start monitoring for user frustration signals
   * @param config Optional configuration to override defaults
   */
  public startMonitoring(config?: Partial<FrustrationDetectionConfig>): void {
    // Prevent duplicate initialization
    if (this.isMonitoring) return;
    
    try {
      // Validate configuration if provided
      if (config) {
        // Create a complete new config object to ensure all properties are properly overridden
        this.config = this.validateConfig({
          ...DEFAULT_CONFIG,  // Start with defaults
          ...config          // Override with custom values
        });
      }
      
      // Rate limiting setup for data collection
      this.setupRateLimiting();
      
      // Setup detection mechanisms with error handling
      this.setupFrustrationDetection();
      this.isMonitoring = true;
      
      console.log(`%c[Frustration Monitor]%c Started monitoring user frustration signals`, 
        'background:#e74c3c; color:white; font-weight: bold; padding: 0 8px', '');
    } catch (error) {
      console.error('Failed to start frustration monitoring:', error);
      this.teardownFrustrationDetection(); // Ensure clean state on failure
    }
  }
  
  /**
   * Validate configuration to ensure values are within acceptable ranges
   * Prevents security issues from malicious configurations
   */
  private validateConfig(config: FrustrationDetectionConfig): FrustrationDetectionConfig {
    // Create safe validated copy with sensible limits
    return {
      rageClickThreshold: Math.max(1, Math.min(10, config.rageClickThreshold)), // Between 1-10
      rageClickTimeFrame: Math.max(100, Math.min(5000, config.rageClickTimeFrame)), // Between 100-5000ms
      deadClickEnabled: Boolean(config.deadClickEnabled),
      formAbandonmentEnabled: Boolean(config.formAbandonmentEnabled),
      navigationThreshingEnabled: Boolean(config.navigationThreshingEnabled),
      errorTrackingEnabled: Boolean(config.errorTrackingEnabled),
      slowInteractionThreshold: Math.max(50, Math.min(2000, config.slowInteractionThreshold)) // Between 50-2000ms
    };
  }
  
  /**
   * Setup rate limiting to prevent excessive data collection
   */
  private setupRateLimiting(): void {
    this.lastDataCollection = Date.now();
    this.collectionThrottleTime = FrustrationMonitor.SECURITY_CONSTANTS.COLLECTION_THROTTLE_TIME; // ms between data collections
    this.maxEventsPerMinute = FrustrationMonitor.SECURITY_CONSTANTS.MAX_EVENTS_PER_MINUTE; // Maximum events recorded per minute
    this.eventCountLastMinute = 0;
    this.lastRateLimitReset = Date.now();
  }
  
  /**
   * Stop monitoring for user frustration and clean up resources
   */
  public stopMonitoring(): void {
    if (!this.isMonitoring) return;
    
    try {
      // Call the teardown method for full cleanup
      this.teardownFrustrationDetection();
      
      // Reset state
      this.isMonitoring = false;
      
      console.log(`%c[Frustration Monitor]%c Stopped monitoring user frustration signals`, 
        'background:#e74c3c; color:white; font-weight: bold; padding: 0 8px', '');
    } catch (error) {
      console.error('Error stopping frustration monitoring:', error);
    }
  }
  
  /**
   * Tear down all event listeners and clean up resources
   * Enhanced with complete cleanup of all event listeners to prevent memory leaks
   */
  private teardownFrustrationDetection(): void {
    try {
      // Create a registry of common event targets for easier cleanup
      const eventTargets = {
        window: window,
        document: document,
        body: document.body
      };
      
      // Clean up all bound event handlers by type
      for (const [key, handler] of Object.entries(this.boundHandlers)) {
        try {
          // Parse event name from the key (may contain target info)
          const parts = key.split('-');
          let eventType = parts[0];
          let targetKey = parts.length > 1 ? parts[1] : 'document'; // Default to document
          
          // Determine the target element for event removal
          let target: EventTarget;
          
          // Handle global targets (window, document, body)
          if (targetKey === 'window' || eventType === 'jsError' || 
              eventType === 'pageUnload' || eventType === 'beforeUnload' || 
              eventType === 'visibilitychange' || eventType === 'popstate') {
            target = window;
          } else if (targetKey === 'document' || 
                     eventType === 'mouseMove' || eventType === 'mousedown' || 
                     eventType === 'mouseup' || eventType === 'click') {
            target = document;
          } else if (targetKey === 'body') {
            target = document.body;
          } else {
            // Try to find element by ID for form handlers
            const element = document.getElementById(targetKey);
            target = element || document; // Fallback to document if element not found
            
            // For form elements, remove both input and change listeners
            if (element && element.tagName === 'FORM') {
              try {
                element.removeEventListener('input', handler);
                element.removeEventListener('change', handler);
              } catch (innerError) {
                console.error(`Error removing form listeners for ${targetKey}:`, innerError);
              }
              continue; // Skip default removal since we've handled form events specially
            }
          }
          
          // Remove the event listener
          try {
            target.removeEventListener(eventType, handler);
          } catch (removalError) {
            console.error(`Error removing event listener ${eventType}:`, removalError);
          }
        } catch (handlerError) {
          console.error(`Error processing handler ${key}:`, handlerError);
          // Continue with other handlers even if one fails
        }
      }
      
      // Clear the bound handlers record
      this.boundHandlers = {};
      
      // Remove any MutationObservers
      if (this.domObserver) {
        this.domObserver.disconnect();
        this.domObserver = null;
      }
      
      // Remove any IntersectionObservers
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
      
      // Restore original fetch function if we've overridden it
      if (this.originalFetch) {
        window.fetch = this.originalFetch;
        this.originalFetch = null;
      }
      
      // Clean up performance observer
      if (this.performanceObserver) {
        try {
          this.performanceObserver.disconnect();
          this.performanceObserver = null;
        } catch (observerError) {
          console.error('Error disconnecting performance observer:', observerError);
        }
      }
      
      // Clear WeakMap references
      this.elementTracker = new WeakMap();
      
      // Reset tracking variables to free memory (use setTimeout for async cleanup)
      setTimeout(() => {
        this.clicks = [];
        this.mouseMovements = [];
        this.navigationHistory = [];
        this.formInteractions = {};
        this.frustrationEvents.length = 0;
      }, 0);
      
    } catch (error) {
      console.error('Error in frustration detection teardown:', error);
    }
  }
  
  /**
   * Setup all frustration detection mechanisms
   */
  private setupFrustrationDetection(): void {
    try {
      // First clean up any existing listeners to prevent duplicates
      this.teardownFrustrationDetection();
      
      // Setup click tracking with error handling
      try {
        this.boundHandlers.click = this.handleClick.bind(this);
        document.addEventListener('click', this.boundHandlers.click);
      } catch (error) {
        console.error('Failed to set up click tracking:', error);
      }
      
      // Setup mouse movement tracking with throttling
      try {
        this.boundHandlers.mouseMove = this.handleMouseMovement.bind(this);
        document.addEventListener('mousemove', this.boundHandlers.mouseMove, { passive: true }); // Using passive for performance
      } catch (error) {
        console.error('Failed to set up mouse movement tracking:', error);
      }
      
      // Setup form tracking if enabled
      if (this.config.formAbandonmentEnabled) {
        try {
          this.setupFormAbandonmentDetection();
        } catch (error) {
          console.error('Failed to set up form tracking:', error);
        }
      }
      
      // Setup slow interaction detection
      try {
        this.setupSlowInteractionDetection();
      } catch (error) {
        console.error('Failed to set up slow interaction detection:', error);
      }
      
      // Setup navigation thrashing detection if enabled
      if (this.config.navigationThreshingEnabled) {
        this.addBoundEventListener(window, 'popstate', this.handleNavigationThrashing);
        this.addBoundEventListener(window, 'hashchange', this.handleNavigationThrashing);
      }
      
      // Setup error tracking if enabled
      if (this.config.errorTrackingEnabled) {
        try {
          this.setupErrorEncounterTracking();
        } catch (error) {
          console.error('Failed to set up error tracking:', error);
        }
      }
      
      // Handle page unload - use once option for the unload event
      try {
        this.boundHandlers.pageUnload = this.handlePageUnload.bind(this);
        window.addEventListener('unload', this.boundHandlers.pageUnload, { once: true });
        
        // Also listen for beforeunload to ensure data is saved
        this.boundHandlers.beforeUnload = this.handlePageUnload.bind(this);
        window.addEventListener('beforeunload', this.boundHandlers.beforeUnload);
      } catch (error) {
        console.error('Failed to set up page unload handling:', error);
      }
      
    } catch (error) {
      console.error('Fatal error in frustration detection setup:', error);
      // Clean up anything that might have been partially set up
      this.teardownFrustrationDetection();
      throw error; // Re-throw to notify the caller
    }
  }
  
  /**
   * Add event listener with proper binding for later removal
   */
  private addBoundEventListener(target: EventTarget, eventName: string, handler: (event: Event) => void): void {
    // Store the bound handler reference for later removal
    const boundHandler = handler.bind(this) as EventListener;
    this.boundHandlers[`${eventName}-${handler.name}`] = boundHandler;
    target.addEventListener(eventName, boundHandler);
  }
  
  /**
   * Setup for detecting slow interactions with the UI
   */
  private setupSlowInteractionDetection(): void {
    // This monitors UI interactions and reports when they take longer than the threshold
    const elementsToMonitor = document.querySelectorAll(
      'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="radio"]'
    );
    
    elementsToMonitor.forEach(el => {
      el.addEventListener('mousedown', (e) => {
        const target = e.target as HTMLElement;
        const startTime = Date.now();
        
        const interactionId = `${target.tagName.toLowerCase()}-${this.getXPath(target)}`;
        this.interactionMeasurements[interactionId] = startTime;
        
        // Listen for mouseup to measure interaction time
        const mouseUpHandler = () => {
          const endTime = Date.now();
          const duration = endTime - startTime;
          
          if (duration > this.config.slowInteractionThreshold) {
            this.recordFrustrationEvent({
              type: FrustrationType.SLOW_INTERACTION,
              timestamp: endTime,
              score: 5, // Medium frustration score
              context: {
                element: {
                  tagName: target.tagName,
                  id: target.id || '',
                  className: target.className?.toString() || '',
                  text: target.textContent?.slice(0, 50) ?? '',
                  href: (target as HTMLAnchorElement).href,
                  xpath: this.getXPath(target)
                },
                location: window.location.pathname,
                details: {
                  duration,
                  threshold: this.config.slowInteractionThreshold
                }
              }
            });
          }
          
          // Cleanup
          document.removeEventListener('mouseup', mouseUpHandler);
          delete this.interactionMeasurements[interactionId];
        };
        
        document.addEventListener('mouseup', mouseUpHandler, { once: true });
      });
    });
  }
  
/** windsurf generated code start */
  /**
   * Setup error encounter tracking (JS errors, API errors) with enhanced security
   */
  private setupErrorEncounterTracking(): void {
    try {
      // Track JavaScript errors with a passive listener to not block rendering
      this.boundHandlers.jsError = this.handleJavaScriptError.bind(this);
      window.addEventListener('error', this.boundHandlers.jsError, { passive: true });
      
      // Intercept fetch API for detecting API errors with security protections
      if (window.fetch && typeof window.fetch === 'function') {
        this.originalFetch = window.fetch;
        
/** windsurf generated code start */
        // Create a secure fetch wrapper with protections against manipulation
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
          // Create a secure copy of the URL and options to prevent tampering during async operations
          const startTime = Date.now();
          
          // Verify this is a legitimate fetch call (security check)
          if (typeof this.isFetchHijackAttempt === 'function' && this.isFetchHijackAttempt(input)) {
            console.warn('Potential fetch hijacking attempt detected and blocked');
            // Fail silently by using the original fetch but not logging the error
            return this.originalFetch ? this.originalFetch(input, init) : fetch(input, init);
          }
          
          // Restrict handling to same-origin URLs for security
          // (Third-party API errors shouldn't count toward user frustration)
          let isMonitorable = false;
          let urlForLogging = 'unknown';
          
          // Determine if this is a same-origin request we should monitor
          if (typeof input === 'string') {
            isMonitorable = this.isSameOriginUrl(input);
            urlForLogging = input;
          } else if (input instanceof URL) {
            isMonitorable = this.isSameOriginUrl(input.href);
            urlForLogging = input.href;
          } else if (input instanceof Request) {
            isMonitorable = this.isSameOriginUrl(input.url);
            urlForLogging = input.url;
          }
          
          try {
            const response = await (this.originalFetch ? this.originalFetch(input, init) : fetch(input, init));
            
            // Only track same-origin API errors
            if (!response.ok && isMonitorable) {
              this.handleApiError(response, urlForLogging, startTime);
            }
            
            return response;
          } catch (error) {
            // Only record frustration for same-origin API errors
            if (isMonitorable) {
              this.recordFrustrationEvent({
                type: FrustrationType.API_ERROR,
                timestamp: Date.now(),
                score: 7,
                context: {
                  error: error instanceof Error ? error.message : String(error),
                  url: urlForLogging,
                  requestTime: Date.now() - startTime,
                  location: window.location.pathname,
                  details: {}
                } as FrustrationEventContext
              });
            }
            
            throw error; // Re-throw to not interfere with normal error handling
          }
        };
/** windsurf generated code end */
      }
    } catch (error) {
      console.error('Failed to setup error tracking:', error);
    }
  }
  
  /**
   * Detect potential fetch hijacking attempts
   * This adds security by checking for suspicious patterns in fetch calls
   */
  private isFetchHijackAttempt(url: RequestInfo | URL): boolean {
    try {
      // Check for obviously malicious URLs
      if (typeof url === 'string') {
        // Check for data URLs or javascript protocol (could be injection attempts)
        if (url.startsWith('data:')) {
          return true;
        }
        
        // Check for extreme URL length (potential DOS vector)
        if (url.length > FrustrationMonitor.SECURITY_CONSTANTS.MAX_URL_LENGTH) {
          return true;
        }
      } else if (url instanceof URL) {
        if (url.protocol === 'data:') {
          return true;
        }
        if (url.href.length > FrustrationMonitor.SECURITY_CONSTANTS.MAX_URL_LENGTH) {
          return true;
        }
      } else if (url instanceof Request) {
        const requestUrl = url.url;
        if (requestUrl.startsWith('data:')) {
          return true;
        }
        if (requestUrl.length > FrustrationMonitor.SECURITY_CONSTANTS.MAX_URL_LENGTH) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking fetch security:', error);
      return true; // Fail closed - treat as hijack attempt if we can't verify
    }
  }
/** windsurf generated code end */

  /**
   * Handle JavaScript errors
   */
  private handleJavaScriptError(event: Event): void {
    try {
      // Cast to ErrorEvent to access error-specific properties
      const errorEvent = event as ErrorEvent;
      const { message, filename, lineno, colno, error } = errorEvent;
      
      this.recordFrustrationEvent({
        type: FrustrationType.JS_ERROR,
        timestamp: Date.now(),
        score: 8, // Higher score for JavaScript errors
        context: {
          error: {
            message,
            name: error?.name ?? 'Unknown Error',
            stack: error?.stack
          },
          location: window.location.pathname,
          details: {
            source: filename,
            line: lineno,
            column: colno
          }
        } as FrustrationEventContext
      });
    } catch (err) {
      console.error('Error handling JavaScript error:', err);
    }
  }

  
  /**
   * Handle API errors from fetch responses
   */
  private handleApiError(response: Response, url: string | Request, startTime: number): void {
    try {
      // Get the URL string
      const urlString = typeof url === 'string' ? url : url.url;
      
      this.recordFrustrationEvent({
        type: FrustrationType.API_ERROR,
        timestamp: Date.now(),
        score: response.status >= 500 ? 7 : 5, // Higher score for server errors
        context: {
          error: `HTTP ${response.status}: ${response.statusText}`,
          url: urlString,
          requestTime: Date.now() - startTime,
          statusCode: response.status,
          location: window.location.pathname,
          details: {
            method: typeof url === 'string' ? 'GET' : url.method || 'GET'
          }
        } as FrustrationEventContext
      });
    } catch (error) {
      console.error('Error handling API error:', error);
    }
  }
  
  /**
   * Check if a URL is same-origin for security purposes
   */
  private isSameOriginUrl(url: string): boolean {
    try {
      // If URL doesn't start with http(s), it's relative and thus same-origin
      if (!/^https?:\/\//i.test(url)) {
        return true;
      }
      
      const currentOrigin = window.location.origin;
      const urlObj = new URL(url);
      return urlObj.origin === currentOrigin;
    } catch {
      // If URL parsing fails, assume it's not same origin
      return false;
    }
  }
  
  /**
   * Record a frustration event with rate limiting
   */
  private recordFrustrationEvent(event: FrustrationEvent): void {
    try {
      const now = Date.now();
      
      // Apply rate limiting to avoid DoS-like behavior or excessive storage usage
      if (now - this.lastDataCollection < this.collectionThrottleTime) {
        return; // Throttle data collection
      }
      
      // Reset rate limit counter every minute
      if (now - this.lastRateLimitReset > 60000) {
        this.eventCountLastMinute = 0;
        this.lastRateLimitReset = now;
      }
      
      // Check if we've exceeded our events per minute limit
      if (this.eventCountLastMinute >= this.maxEventsPerMinute) {
        return; // Exceeded rate limit
      }
      
      // Update counters
      this.lastDataCollection = now;
      this.eventCountLastMinute++;
      
      // Sanitize event data to prevent XSS in stored data
      const sanitizedEvent = this.sanitizeFrustrationEvent(event);
      this.frustrationEvents.push(sanitizedEvent);
      
      // Log the event for debugging in development
      console.log(`%c[Frustration Monitor]%c ${sanitizedEvent.type} detected (score: ${sanitizedEvent.score})`, 
        'background:#e74c3c; color:white; font-weight: bold; padding: 0 8px', '',
        sanitizedEvent.context);
      
      // Save to storage for persistence
      this.saveFrustrationToStorage(sanitizedEvent);
    } catch (error) {
      console.error('Error recording frustration event:', error);
    }
  }
  
/** windsurf generated code start */
  /**
   * Sanitize event data to prevent XSS in stored data
   */
  private sanitizeFrustrationEvent(event: FrustrationEvent): FrustrationEvent {
    try {
      // Create a deep copy to avoid mutation
      const sanitized = { ...event } as FrustrationEvent;
      sanitized.context = { ...event.context } as FrustrationEventContext;
      
      // Sanitize text content to prevent XSS
      if (sanitized.context.element) {
        sanitized.context.element = { ...sanitized.context.element };
        
        // Truncate and sanitize text fields
        if (sanitized.context.element.text) {
          sanitized.context.element.text = this.sanitizeString(sanitized.context.element.text, 100);
        }
        
        if (sanitized.context.element.className) {
          sanitized.context.element.className = this.sanitizeString(sanitized.context.element.className, 100);
        }
      }
      
      // Sanitize location if present
      if (sanitized.context.location) {
        sanitized.context.location = this.sanitizeString(sanitized.context.location, 200);
      }
      
      // Sanitize any errors in context - using type guard to handle the optional error property
      if (hasError(sanitized.context)) {
        if (typeof sanitized.context.error === 'string') {
          // String error
          sanitized.context.error = this.sanitizeString(sanitized.context.error, 500);
        } else if (sanitized.context.error && typeof sanitized.context.error === 'object') {
          // Error object with message
          const errorObj = sanitized.context.error as { message?: string };
          if (errorObj.message) {
            errorObj.message = this.sanitizeString(errorObj.message, 500);
          }
        }
      }
      
      // Sanitize URL if present
      if (hasUrl(sanitized.context)) {
        sanitized.context.url = this.sanitizeString(sanitized.context.url, 500);
      }
      
      return sanitized;
    } catch (error) {
      console.error('Error sanitizing event data:', error);
      return event; // Return original in case of error
    }
  }
/** windsurf generated code end */
  
/** windsurf generated code start */
  /**
   * Constants for security and performance settings
   */
  static readonly SECURITY_CONSTANTS = {
    // Security constants for limiting input lengths and rates
    MAX_STRING_LENGTH: 1000,
    MAX_ERROR_MESSAGE_LENGTH: 500,
    MAX_URL_LENGTH: 500,
    MAX_LOCATION_LENGTH: 200,
    MAX_ELEMENT_TEXT_LENGTH: 100,
    MAX_SESSION_STORAGE_SIZE: 2000000, // ~2MB
    MAX_EVENTS_PER_SESSION: 1000,
    // Rate limiting constants
    COLLECTION_THROTTLE_TIME: 500, // ms
    MAX_EVENTS_PER_MINUTE: 60,
    MAX_MOUSE_MOVEMENTS: 50,
    MOUSE_MOVEMENT_THROTTLE: 100, // ms
/** windsurf generated code end */  
    // Storage security limits
    MAX_STORAGE_ITEMS: 100,
    MAX_STORAGE_SIZE_BYTES: 2 * 1024 * 1024, // 2MB limit for frustration data
    
    // Performance thresholds
    MOUSE_THROTTLE_MS: 100, // Throttle mouse events to every 100ms
    MAX_MOUSE_EVENTS: 50, // Maximum number of mouse events to store
    MAX_MOUSE_EVENT_AGE_MS: 5000, // Only keep mouse events from last 5 seconds
    INPUT_DEBOUNCE_MS: 300 // Debounce input events
  };

  /**
   * Comprehensive sanitization of string data to prevent XSS
   * Uses multiple techniques to ensure security
   */
  private sanitizeString(input: string | undefined | null, maxLength: number = FrustrationMonitor.SECURITY_CONSTANTS.MAX_STRING_LENGTH): string {
    if (!input) return '';
    
    try {
      // Ensure string type and truncate to reasonable length
      let sanitized = String(input).slice(0, maxLength);
      
      // Remove all script and event handler content
      sanitized = sanitized
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, 'data-removed=')
        .replace(/data-on\w+\s*=/gi, 'data-removed=');
      
      // Encode all HTML special characters to prevent tag insertion
      sanitized = sanitized
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/`/g, '&#x60;')
        .replace(/\//g, '&#x2F;');
      
      // Remove potential Unicode character exploits
      sanitized = sanitized
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Control characters
        .replace(/[\u2000-\u2029]/g, ' '); // Weird spaces and Unicode whitespace
      
      return sanitized.trim();
    } catch (error) {
      console.error('Error in string sanitization:', error);
      // If anything goes wrong, return empty string as failsafe
      return '';
    }
  }
/** windsurf generated code end */
/** windsurf generated code end */
  
  /**
   * Save frustration event to storage with proper size limitations and security measures
   */
  private saveFrustrationToStorage(event: FrustrationEvent): void {
    try {
      // First check if we're approaching storage limits (2MB cap as set in SECURITY_CONSTANTS)
      if (this.isStorageNearingCapacity()) {
        // If we're nearing capacity, remove oldest events
        this.pruneOldStorageEntries();
        return; // Skip saving this event if we're at capacity
      }
      
      // Get existing frustration data
      const frustrationDataStr = sessionStorage.getItem('opus2_frustration_events') ?? '[]';
      let frustrationData: any[];
      
      try {
        frustrationData = JSON.parse(frustrationDataStr);
        if (!Array.isArray(frustrationData)) {
          // Reset if data is corrupted
          frustrationData = [];
        }
      } catch (parseError) {
        // Reset storage if corrupted
        console.error('Error parsing frustration data, resetting storage:', parseError);
        frustrationData = [];
      }
      
      // Add new event with security measures
      const safeEvent = {
        type: this.sanitizeString(event.type, 50),
        timestamp: new Date(event.timestamp).toISOString(),
        score: Math.min(Math.max(event.score, 1), 10), // Ensure score is between 1-10
        context: this.sanitizeFrustrationEvent(event).context
      };
      
      frustrationData.push(safeEvent);
      
      // Keep only the most recent events within our security limits
      if (frustrationData.length > FrustrationMonitor.SECURITY_CONSTANTS.MAX_STORAGE_ITEMS) {
        frustrationData = frustrationData.slice(-FrustrationMonitor.SECURITY_CONSTANTS.MAX_STORAGE_ITEMS);
      }
      
      // Compress data if needed to reduce storage footprint
      const serializedData = JSON.stringify(frustrationData);
      if (serializedData.length > FrustrationMonitor.SECURITY_CONSTANTS.MAX_STORAGE_SIZE_BYTES) {
        // If still too large after pruning, keep removing oldest items
        while (frustrationData.length > 1) {
          frustrationData.shift(); // Remove oldest
          const testSize = JSON.stringify(frustrationData).length;
          if (testSize <= FrustrationMonitor.SECURITY_CONSTANTS.MAX_STORAGE_SIZE_BYTES) {
            break;
          }
        }
      }
      
      // Save back to storage with try-catch to handle quota exceeded errors
      try {
        sessionStorage.setItem('opus2_frustration_events', JSON.stringify(frustrationData));
      } catch (storageError) {
        console.warn('Storage quota exceeded, clearing old data', storageError);
        // If quota error, clear half of the oldest events
        if (frustrationData.length > 2) {
          frustrationData = frustrationData.slice(Math.floor(frustrationData.length / 2));
          try {
            sessionStorage.setItem('opus2_frustration_events', JSON.stringify(frustrationData));
          } catch (secondError) {
            // Last resort: clear all data if we still can't store
            sessionStorage.removeItem('opus2_frustration_events');
            console.warn('Storage full, had to clear all frustration data', secondError);
          }
        }
      }
    } catch (error) {
      console.warn('Error saving frustration data to storage:', error);
    }
  }

  /**
   * Check if browser storage is nearing capacity
   */
  private isStorageNearingCapacity(): boolean {
    try {
      // Calculate approximate size of current storage
      const events = sessionStorage.getItem('opus2_frustration_events') ?? '[]';
      const summaries = localStorage.getItem('opus2_frustration_summaries') ?? '[]';
      const totalSize = events.length + summaries.length;
      
      // If approaching 80% of our self-imposed limit, return true
      return totalSize > (FrustrationMonitor.SECURITY_CONSTANTS.MAX_STORAGE_SIZE_BYTES * 0.8);
    } catch (error) {
      console.error('Error checking storage capacity:', error);
      return true; // Assume we're at capacity if we can't check
    }
  }

  /**
   * Remove oldest entries from storage to free up space
   */
  private pruneOldStorageEntries(): void {
    try {
      // Prune session events
      const events = JSON.parse(sessionStorage.getItem('opus2_frustration_events') ?? '[]');
      if (Array.isArray(events) && events.length > 10) {
        // Keep only the 10 most recent events
        const prunedEvents = events.slice(-10);
        sessionStorage.setItem('opus2_frustration_events', JSON.stringify(prunedEvents));
      }
      
      // Prune local storage summaries
      const summaries = JSON.parse(localStorage.getItem('opus2_frustration_summaries') ?? '[]');
      if (Array.isArray(summaries) && summaries.length > 10) {
        // Keep only the 10 most recent summaries
        const prunedSummaries = summaries.slice(-10);
        localStorage.setItem('opus2_frustration_summaries', JSON.stringify(prunedSummaries));
      }
    } catch (error) {
      console.error('Error pruning storage:', error);
      // If pruning fails, clear all data as a last resort
      try {
        sessionStorage.removeItem('opus2_frustration_events');
        localStorage.removeItem('opus2_frustration_summaries');
      } catch (clearError) {
        console.error('Failed to clear storage:', clearError);
      }
    }
  }
  
  /**
   * Handle page unload - save accumulated data
   */
  private handlePageUnload(event?: Event): void {
    // If we have frustration events, save a summary to localStorage
    if (this.frustrationEvents.length > 0) {
      try {
        const summary = {
          timestamp: new Date().toISOString(),
          page: window.location.pathname,
          totalFrustrationScore: this.frustrationEvents.reduce((total, ev) => total + ev.score, 0),
          eventCounts: this.getEventTypeCounts(),
        };
        
        // Get existing summaries
        const summariesStr = localStorage.getItem('opus2_frustration_summaries') ?? '[]';
        const summaries = JSON.parse(summariesStr);
        
        // Add new summary and limit to last 50
        summaries.push(summary);
        if (summaries.length > 50) {
          summaries.shift();
        }
        
        // Save back to storage
        localStorage.setItem('opus2_frustration_summaries', JSON.stringify(summaries));
      } catch (error) {
        console.error('Error saving frustration summary:', error);
      }
    }
  }
  
  /**
   * Get counts of each event type
   */
  private getEventTypeCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    
    for (const event of this.frustrationEvents) {
      counts[event.type] = (counts[event.type] ?? 0) + 1;
    }
    
    return counts;
  }
  
  /**
   * Setup detection of dead clicks (clicks on non-interactive elements)
   */
  private setupDeadClickDetection(): void {
    // Dead clicks are detected through the click handler which is already set up
    // by the rage click detection, so we don't need to add another event listener
  }
  
  /**
   * Setup form abandonment detection
   */
  private setupFormAbandonmentDetection(): void {
    // Add listeners for form interactions
    this.addBoundEventListener(document, 'input', this.handleFormInput);
    this.addBoundEventListener(document, 'submit', this.handleFormSubmit);
    
    // Track navigation events for thrashing detection
    this.addBoundEventListener(window, 'popstate', this.handleNavigationThrashing);
    this.addBoundEventListener(window, 'hashchange', this.handleNavigationThrashing);
  }
  
  /**
   * Handle form input events to track form interactions
   */
  private handleFormInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const form = target.closest('form');
    
    if (!form) return;
    
    const formIdentifier = form.id || form.action || form.name || this.getXPath(form);
    
    // Initialize form tracking if not already tracking this form
    if (!this.formInteractions[formIdentifier]) {
      this.formInteractions[formIdentifier] = {
        startTime: Date.now(),
        fields: []
      };
    }
    
    // Add field if not already tracked
    const fieldIdentifier = target.name || target.id || this.getXPath(target);
    if (!this.formInteractions[formIdentifier].fields.includes(fieldIdentifier)) {
      this.formInteractions[formIdentifier].fields.push(fieldIdentifier);
    }
  }
  
  /**
   * Handle form submission
   */
  private handleFormSubmit(event: Event): void {
    const form = event.target as HTMLFormElement;
    const formIdentifier = form.id || form.action || form.name || this.getXPath(form);
    
    // Remove from tracked forms since it was successfully submitted
    delete this.formInteractions?.[formIdentifier];
  }
  
  /**
   * Setup detection for excessive navigation (user seems lost)
   */
  private setupNavigationThrashingDetection(): void {
    // Record initial page view
    this.navigationHistory.push({
      path: window.location.pathname,
      timestamp: Date.now()
    });
    
    // This requires integration with the router in a real app
    // For this demo, we'll just listen for popstate events
    this.addBoundEventListener(window, 'popstate', this.handleNavigationThrashing);
    
    // We'd also need to intercept navigation events from the router
    // For a simple example, let's just track hash changes
    this.addBoundEventListener(window, 'hashchange', this.handleNavigationThrashing);
  }
  
  // Navigation handling has been consolidated into handleNavigationThrashing
  
  /**
   * Handle navigation events with thrashing detection
   * This is the preferred implementation that should be used
   */
  private handleNavigationThrashing(event: Event): void {
    try {
      const now = Date.now();
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      
      // Record this navigation
      this.navigationHistory.push({
        path: currentPath,
        timestamp: now
      });
      
      // Keep only recent navigation history (last minute)
      this.navigationHistory = this.navigationHistory.filter(n => now - n.timestamp <= 60000);
      
      // Check for navigation thrashing (back and forth between pages)
      if (this.navigationHistory.length >= 4) {
        const paths = new Set<string>();
        
        // Check if we have at least one repeated navigation within a short timeframe
        this.navigationHistory.forEach(nav => paths.add(nav.path));
        
        if (paths.size < this.navigationHistory.length) {
          // Find repeating patterns
          const lastNavs = this.navigationHistory.slice(-4);
          const timespan = lastNavs[lastNavs.length - 1].timestamp - lastNavs[0].timestamp;
          
          // If 4+ navigations in less than 20 seconds with repeats, consider it thrashing
          if (timespan < 20000) {
            this.recordFrustrationEvent({
              type: FrustrationType.NAVIGATION_THRASHING,
              timestamp: now,
              score: 6, // Higher score for navigation thrashing
              context: {
                location: currentPath,
                details: {
                  navigationCount: this.navigationHistory.length,
                  timespan,
                  uniquePaths: Array.from(paths)
                }
              } as FrustrationEventContext
            });
          }
        }
      }
    } catch (error) {
      console.error('Error handling navigation event:', error);
    }
  }
  
  /**
   * Get global frustration insights from storage
   */
  public static getFrustrationInsights(): string {
    try {
      // Get session events
      const sessionDataStr = sessionStorage.getItem('opus2_frustration_events') ?? '[]';
      const sessionEvents = JSON.parse(sessionDataStr);
      
      // Get history summaries
      const summariesStr = localStorage.getItem('opus2_frustration_summaries') ?? '[]';
      const summaries = JSON.parse(summariesStr);
      
      if (sessionEvents.length === 0 && summaries.length === 0) {
        console.log('%c[Frustration Insights]%c No frustration data available', 
          'background:#e74c3c; color:white; font-weight: bold; padding: 0 8px', '');
        return 'No frustration data available';
      }
      
      return FrustrationMonitor.logFrustrationInsights(sessionEvents, summaries);
    } catch (error) {
      console.error('Error retrieving frustration insights:', error);
      return `Error retrieving frustration insights: ${error instanceof Error ? error.message : String(error)}`;
    }
  }
  
  private static logFrustrationInsights(sessionEvents: any[], summaries: any[]): string {
    // Log current session events
    console.group('%c[Frustration Insights]%c Current Session', 
      'background:#e74c3c; color:white; font-weight: bold; padding: 0 8px', '');
    
    if (sessionEvents.length > 0) {
      FrustrationMonitor.logSessionEvents(sessionEvents);
    } else {
      console.log('No frustration events in current session');
    }
    
    console.groupEnd();
    
    // Log historical summaries if available
    if (summaries.length > 0) {
      FrustrationMonitor.logHistoricalSummaries(summaries);
    }
    
    return `Found ${sessionEvents.length} frustration events in current session and ${summaries.length} historical summaries`;
  }
  
  private static logSessionEvents(sessionEvents: any[]): void {
    sessionEvents.forEach((event: any) => {
      console.group(`${event.type} (score: ${event.score})`);
      console.log('Timestamp:', new Date(event.timestamp).toLocaleTimeString());
      console.log('Context:', event.context);
      console.groupEnd();
    });
    
    // Calculate total frustration score
    const totalScore = sessionEvents.reduce((sum: number, event: any) => sum + event.score, 0);
    console.log(`Total Frustration Score: ${totalScore}`);
  }
  
  private static logHistoricalSummaries(summaries: any[]): void {
    console.group('%c[Frustration History]%c Previous Sessions', 
      'background:#e74c3c; color:white; font-weight: bold; padding: 0 8px', '');
    
    summaries.forEach((summary: any) => {
      console.group(`${new Date(summary.timestamp).toLocaleDateString()} - ${summary.page}`);
      console.log(`Frustration Score: ${summary.totalFrustrationScore}`);
      console.log('Event Counts:', summary.eventCounts);
      console.groupEnd();
    });
    
    console.groupEnd();
  }
  
  /**
   * Handle click events to detect rage clicks and dead clicks
   */
  private handleClick(event: Event): void {
    try {
      const now = Date.now();
      // Cast to MouseEvent for access to coordinates
      const mouseEvent = event as MouseEvent;
      const clickInfo = {
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
        timestamp: now
      };
      
      // Store the click for tracking
      this.clicks.push(clickInfo);
      
      // Remove clicks outside of the rage click time window
      const timeWindow = this.config.rageClickTimeFrame;
      this.clicks = this.clicks.filter(click => now - click.timestamp <= timeWindow);
      
      // Check for rage clicks (multiple clicks in same area within timeframe)
      if (this.clicks.length >= this.config.rageClickThreshold) {
        const closeClicks = this.countCloseClicks(clickInfo);
        
        if (closeClicks >= this.config.rageClickThreshold) {
          // We have a rage click situation
          this.recordFrustrationEvent({
            type: FrustrationType.RAGE_CLICK,
            timestamp: now,
            score: 7, // High frustration score for rage clicks
            context: {
              element: this.getElementInfo(event.target as HTMLElement),
              location: window.location.pathname,
              details: {
                clickCount: closeClicks,
                timeWindow
              }
            } as FrustrationEventContext
          });
        }
      }
      
      // Check for dead clicks (clicks on non-interactive elements)
      this.detectDeadClick(mouseEvent);
    } catch (error) {
      console.error('Error handling click event:', error);
    }
  }
  
  /**
   * Count clicks that are close to the given click position
   */
  private countCloseClicks(click: {x: number, y: number, timestamp: number}): number {
    // Consider clicks within 30px to be in the same area
    const proximityThreshold = 30;
    
    return this.clicks.filter(c => 
      Math.abs(c.x - click.x) <= proximityThreshold && 
      Math.abs(c.y - click.y) <= proximityThreshold
    ).length;
  }
  
  /**
   * Detect dead clicks (clicks on non-interactive elements)
   */
  private detectDeadClick(event: MouseEvent): void {
    if (!this.config.deadClickEnabled) return;
    
    const target = event.target as HTMLElement;
    
    // Check if element is not interactive
    const isInteractive = this.isInteractiveElement(target);
    
    if (!isInteractive) {
      this.recordFrustrationEvent({
        type: FrustrationType.DEAD_CLICK,
        timestamp: Date.now(),
        score: 4, // Medium frustration score for dead clicks
        context: {
          element: this.getElementInfo(target),
          location: window.location.pathname,
          details: {
            eventType: event.type
          }
        } as FrustrationEventContext
      });
    }
  }
  
  /**
   * Check if an element is interactive
   */
  private isInteractiveElement(element: HTMLElement): boolean {
    // Common interactive elements
    const interactiveTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'OPTION', 'LABEL'];
    
    // Check tag name
    if (interactiveTags.includes(element.tagName)) {
      return true;
    }
    
    // Check for elements with click event handlers using role attributes
    const interactiveRoles = ['button', 'link', 'checkbox', 'menuitem', 'menuitemcheckbox', 
      'menuitemradio', 'option', 'radio', 'searchbox', 'switch', 'tab'];
    
    const role = element.getAttribute('role');
    if (role && interactiveRoles.includes(role)) {
      return true;
    }
    
    // Check for elements with common interactive class names
    const elementClasses = element.className.split(' ');
    const interactiveClassPatterns = ['button', 'btn', 'clickable', 'link'];
    
    if (elementClasses.some(cls => 
        interactiveClassPatterns.some(pattern => cls.toLowerCase().includes(pattern)))) {
      return true;
    }
    
    // Check for cursor style indicating interactivity
    const computedStyle = window.getComputedStyle(element);
    if (computedStyle.cursor === 'pointer') {
      return true;
    }
    
    // Check if element has click-related attributes
    if (element.hasAttribute('onclick') || element.hasAttribute('ng-click') || 
        element.hasAttribute('@click') || element.hasAttribute('v-on:click')) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Get information about an HTML element for reporting
   */
  private getElementInfo(element: HTMLElement | null): {
    tagName: string;
    id: string;
    className: string;
    text: string;
    href?: string;
    xpath: string;
  } | undefined {
    if (!element) return undefined;
    
    try {
      // Basic element info with optional href property
      const result: {
        tagName: string;
        id: string;
        className: string;
        text: string;
        xpath: string;
        href?: string;
      } = {
        tagName: element.tagName?.toLowerCase() || 'unknown',
        id: element.id || '',
        className: element.className || '',
        text: element.textContent ? 
          this.sanitizeString(element.textContent) : '',
        xpath: this.getXPath(element)
      };
      
      // Add href for links, using optional chaining for better safety
      if (element.tagName === 'A') {
        const href = (element as HTMLAnchorElement)?.href;
        if (href) {
          // Sanitize URL to prevent XSS through javascript: URLs
          const sanitizedHref = href.startsWith('javascript:') ? 
            '' : 
            href.replace(/javascript:(\s*\{[^}]+\})/, 'javascript:void(0)');
          if (sanitizedHref) {
            result.href = sanitizedHref;
          }
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error getting element info:', error);
      return undefined;
    }
  }
  
  /**
   * Get XPath for an element
   */
  private getXPath(element: Node): string {
    try {
      // Handle the case where element is null
      if (!element?.nodeType) return '';
      
      // Special case for document
      if (element.nodeType === Node.DOCUMENT_NODE) return '/';
      
      // Use path segments to build the path
      const segments: string[] = [];
      let current: Node | null = element;
      
      while (current && current.nodeType === Node.ELEMENT_NODE) {
        // Find position among siblings
        let position = 1;
        let sibling = current.previousSibling;
        
        while (sibling) {
          if (sibling.nodeType === Node.ELEMENT_NODE && 
              (sibling as Element).tagName === (current as Element).tagName) {
            position++;
          }
          sibling = sibling.previousSibling;
        }
        
        // Build path segment
        const tagName = (current as Element).tagName.toLowerCase();
        segments.unshift(`${tagName}[${position}]`);
        
        // Move up to parent
        current = current.parentNode;
      }
      
      return '/' + segments.join('/');
    } catch (error) {
      console.error('Error generating XPath:', error);
      return '';
    }
  }
  
  /**
   * Handle mouse movement events for tracking user interactions
   */
  private handleMouseMovement(event: Event): void {
    try {
      const now = Date.now();
      const mouseEvent = event as MouseEvent;
      
      // Throttle mouse movement tracking
      if (now - this.lastMouseMoveTime < FrustrationMonitor.SECURITY_CONSTANTS.MOUSE_THROTTLE_MS) {
        return;
      }
      
      this.lastMouseMoveTime = now;
      
      // Add to tracking
      this.mouseMovements.push({
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
        timestamp: now  // Using timestamp consistently instead of time
      });
      
      // Limit array size for performance
      if (this.mouseMovements.length > FrustrationMonitor.SECURITY_CONSTANTS.MAX_MOUSE_EVENTS) {
        this.mouseMovements = this.mouseMovements.slice(-FrustrationMonitor.SECURITY_CONSTANTS.MAX_MOUSE_EVENTS);
      }
    } catch (error) {
      console.error('Error handling mouse movement:', error);
    }
  }
}

/**
 * Start frustration monitoring with default configuration
 */
export function initFrustrationMonitoring(config?: Partial<FrustrationDetectionConfig>): void {
  const monitor = FrustrationMonitor.getInstance();
  monitor.startMonitoring(config);
  // Logging is already done inside startMonitoring method
}

/**
 * Get frustration insights and print them to console
 */
export function getFrustrationInsights(): string {
  return FrustrationMonitor.getFrustrationInsights();
}

/** windsurf generated code start */
/**
 * Initialize window global exposure with security protections
 */
export function exposeToWindow(): void {
  // Prevent potential XSS by using a safe wrapper function that sanitizes inputs and outputs
  try {
    Object.defineProperty(window, 'getFrustrationInsights', {
      value: function() {
        // Only allow this function to run in appropriate contexts
        const isSecureContext = window.isSecureContext;
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
        
        if (!isSecureContext && !isLocalhost) {
          console.warn('getFrustrationInsights is only available in secure contexts or localhost');
          return 'Access denied: Available only in secure contexts or development environments';
        }
        
        // Safe wrapper for the actual function
        try {
          return getFrustrationInsights();
        } catch (error) {
          console.error('Error in getFrustrationInsights:', error);
          return 'An error occurred while retrieving frustration insights';
        }
      },
      writable: false,
      configurable: false
    });
  } catch (error) {
    console.error('Failed to expose functions to window safely:', error);
  }
}
/** windsurf generated code end */
